/**
 * RhymeResultChip - Collection-aware word chip for rhyme search results
 *
 * Shows collected state with checkmark, handles optimistic toggle on click.
 * Uses color mapping and tooltips from existing RhymeChip design.
 */

import React from 'react'
import type { RhymeType, SyllableCount } from '../../types/wordCollection'

// Color mappings from RhymeChip.tsx
const RHYME_COLORS = {
  perfect: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700' },
  family: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700' },
  additive: { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-700' },
  subtractive: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-700' },
  assonance: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700' },
  consonance: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700' }
} as const

// Tooltips from RhymeChip.tsx
const RHYME_TOOLTIPS: Record<RhymeType, string> = {
  perfect: 'Perfect Rhyme - Identical vowel and consonant endings',
  family: 'Family Rhyme - Similar consonant patterns',
  additive: 'Additive Rhyme - Base word with additional sounds',
  subtractive: 'Subtractive Rhyme - Base word minus sounds',
  assonance: 'Assonance - Same vowel sounds, different consonants',
  consonance: 'Consonance - Same consonant sounds, different vowels'
}

interface RhymeResultChipProps {
  /** The word to display */
  word: string
  /** Rhyme type for color coding */
  rhymeType: RhymeType
  /** Syllable count (1-4) */
  syllableCount: SyllableCount
  /** Is this word already collected? */
  isCollected: boolean
  /** Click handler for collection toggle */
  onClick: () => void
  /** Optional subcategory for family rhymes */
  subcategory?: string
}

/**
 * Collection-aware chip component for rhyme search results.
 *
 * Visual states:
 * - Uncollected: Outlined chip with transparent background
 * - Collected: Filled chip with checkmark indicator
 *
 * Click behavior toggles collection state optimistically.
 */
export const RhymeResultChip = React.memo<RhymeResultChipProps>(({
  word,
  rhymeType,
  syllableCount,
  isCollected,
  onClick,
  subcategory
}) => {
  const colors = RHYME_COLORS[rhymeType]
  const tooltip = RHYME_TOOLTIPS[rhymeType]

  return (
    <button
      onClick={onClick}
      title={tooltip}
      className={`
        px-2 py-1 text-sm rounded border transition-all
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
        ${colors.border} ${colors.text}
        ${isCollected
          ? `${colors.bg} font-medium`
          : 'bg-white hover:bg-gray-50'
        }
      `}
      data-testid="rhyme-result-chip"
      data-rhyme-type={rhymeType}
      data-collected={isCollected}
      data-syllable-count={syllableCount}
      data-subcategory={subcategory}
      aria-label={`${isCollected ? 'Remove' : 'Add'} ${word} to collection`}
    >
      {word}
      {isCollected && (
        <span
          className="ml-1.5 text-xs opacity-75"
          data-testid="rhyme-chip-collected"
          aria-label="Already collected"
        >
          ✓
        </span>
      )}
    </button>
  )
})

RhymeResultChip.displayName = 'RhymeResultChip'
